import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { FormGridService } from '@modules/forms/components/form-grid/services/form-grid.service';
import { getSadmRoute } from '@modules/sadm/utils/get-sadm-route';
import { Store } from '@ngrx/store';
import { Status } from '@shared/models/enums/status.enum';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { selectDepartmentId } from '@shared/store/router/router.selectors';
import { injectActiveClientCode } from '@shared/utils/active-client-code.utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SadmGridEntity } from '../models/sadm-grid-entity';
import { SadmGridStore } from './sadm-grid-store';

@Injectable()
export class SadmGridHeaderActionsService {
  private readonly translocoService = inject(TranslocoService);
  private readonly formGridService = inject(FormGridService);
  private readonly sadmGridStore = inject(SadmGridStore);
  private readonly router = inject(Router);
  private readonly entities$ = toObservable(this.sadmGridStore.entities);
  private readonly clientCode = injectActiveClientCode();
  private readonly departmentId = inject(Store).selectSignal(selectDepartmentId);

  getActions(): MenuAction<SadmGridEntity>[] {
    return [
      {
        id: MenuActionId.SADM_GRID_DRAFT_DELETE,
        icon: '@tui.x',
        hint: this.translocoService.translate('Supprimer'),
        status: Status.ERROR,
        condition$: (entity: SadmGridEntity): Observable<boolean> =>
          this.entities$.pipe(map((entities) => entity.sadmEntityId === null && entities.length > 1)),
        execute: (entity: SadmGridEntity): void => this.removeDraft(entity),
      },
      {
        id: MenuActionId.SADM_GRID_DASHBOARD_LINK,
        icon: '@tui.line-chart',
        hint: this.translocoService.translate("Ouvrir le bilan d'évolution"),
        condition: (entity: SadmGridEntity): boolean => entity.sadmEntityId !== null,
        execute: (entity: SadmGridEntity): void => {
          if (entity.sadmEntityId === null) {
            return;
          }
          // The grid is a dialog: same-tab navigation would unmount it, so open the
          // evolution dashboard in a new tab instead.
          const route = getSadmRoute(this.clientCode(), this.departmentId(), entity.sadmEntityId);
          const url = this.router.serializeUrl(this.router.createUrlTree(route));
          window.open(url, '_blank');
        },
      },
      // TODO: re-enable when lesion duplication is implemented.
      // {
      //   id: MenuActionId.SADM_GRID_DUPLICATE,
      //   icon: '@tui.copy',
      //   hint: this.translocoService.translate('Dupliquer la lésion'),
      //   condition: (entity: SadmGridEntity): boolean => entity.sadmEntityId !== null,
      //   execute: (entity: SadmGridEntity): void => this.duplicate(entity),
      // },
    ];
  }

  getActionButtons(): ActionButton[] {
    return [
      {
        actionIds: [MenuActionId.SADM_GRID_DRAFT_DELETE],
        icon: '@tui.x',
      },
      {
        actionIds: [MenuActionId.SADM_GRID_DASHBOARD_LINK],
        icon: '@tui.line-chart',
      },
      // TODO: re-enable when lesion duplication is implemented.
      // {
      //   actionIds: [MenuActionId.SADM_GRID_DUPLICATE],
      //   icon: '@tui.copy',
      // },
    ];
  }

  private removeDraft(entity: SadmGridEntity): void {
    if (entity.sadmEntityId !== null || this.sadmGridStore.entities().length <= 1) {
      return;
    }
    this.formGridService.removeEntity(entity.entityKey);
    this.sadmGridStore.removeDraft(entity.entityKey);
  }
}
